import React, { useState, useEffect, useRef } from 'react';
import { Button, Form } from 'react-bootstrap';
import { SketchPicker } from 'react-color';
import { FaTrash, FaStar, FaChevronLeft, FaChevronRight, FaEraser, FaBroom } from 'react-icons/fa';
import './DrawingBoard.css';

function DrawingBoard({ darkMode }) {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [width, setWidth] = useState(5);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [favoritePens, setFavoritePens] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const startDrawing = (e) => {
    setIsDrawing(true);
    setLastPos({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
  };

  const draw = (e) => {
  if (!isDrawing) return;
  const ctx = canvasRef.current.getContext('2d');

  // If eraser, force black color
  if (tool === 'eraser') {
    ctx.strokeStyle = '#000000';
  } else {
    ctx.strokeStyle = color;
  }

  ctx.lineWidth =
    tool === 'marker'
      ? width * 2
      : tool === 'brush'
      ? width * 1.5
      : width;

  ctx.beginPath();
  ctx.moveTo(lastPos.x, lastPos.y);
  ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
  ctx.stroke();
  setLastPos({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
};


  const stopDrawing = () => setIsDrawing(false);

  const addFavoritePen = () => {
    setFavoritePens([...favoritePens, { tool, color, width }]);
  };

  const applyFavoritePen = (pen) => {
    setTool(pen.tool);
    setColor(pen.color);
    setWidth(pen.width);
  };

  const removeFavoritePen = (index) => {
    setFavoritePens(favoritePens.filter((_, i) => i !== index));
  };

  const clearBoard = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  return (
    <div className="drawingboard-container">
      {sidebarOpen && (
        <div
          className={`drawingboard-sidebar ${
            darkMode ? 'drawingboard-sidebar-dark' : 'drawingboard-sidebar-light'
          }`}
        >
          <h5 className="drawingboard-favpens-title">🎨 Favorite Pens</h5>
          {favoritePens.length === 0 && (
            <p style={{ fontSize: '0.9rem', color: darkMode ? '#ccc' : '#555' }}>
              No favorites yet.
            </p>
          )}
          {favoritePens.map((pen, index) => (
            <div
              key={index}
              className={`drawingboard-favpen ${
                darkMode ? 'drawingboard-favpen-dark' : 'drawingboard-favpen-light'
              }`}
            >
              <div
                className="drawingboard-favpen-info"
                onClick={() => applyFavoritePen(pen)}
              >
                <div
                  className="drawingboard-favpen-color"
                  style={{ backgroundColor: pen.color }}
                />
                <span style={{ fontSize: '0.85rem' }}>
                  {pen.tool} ({pen.width}px)
                </span>
              </div>
              <Button variant="danger" size="sm" onClick={() => removeFavoritePen(index)}>
                <FaTrash />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button
        variant="secondary"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="drawingboard-toggle-btn"
      >
        {sidebarOpen ? <FaChevronLeft /> : <FaChevronRight />}
      </Button>

      <div style={{ flexGrow: 1, position: 'relative' }}>
        <div className="drawingboard-controls">
          <Button
            variant={tool === 'pen' ? 'primary' : 'outline-primary'}
            onClick={() => setTool('pen')}
          >
            Pen
          </Button>
          <Button
            variant={tool === 'marker' ? 'primary' : 'outline-primary'}
            onClick={() => setTool('marker')}
          >
            Marker
          </Button>
          <Button
            variant={tool === 'brush' ? 'primary' : 'outline-primary'}
            onClick={() => setTool('brush')}
          >
            Brush
          </Button>
          <Button
            variant={tool === 'eraser' ? 'primary' : 'outline-secondary'}
            onClick={() => setTool('eraser')}
          >
            <FaEraser /> Eraser
          </Button>
          <Button
            variant="danger"
            onClick={clearBoard}
          >
            <FaBroom /> Clear
          </Button>
          <Button
            variant="warning"
            onClick={() => setShowColorPicker(!showColorPicker)}
          >
            Pick Color
          </Button>
          <Form.Control
            type="number"
            value={width}
            min="1"
            max="50"
            onChange={(e) => setWidth(Number(e.target.value))}
            placeholder="Width"
            style={{ width: '80px' }}
          />
          <Button variant="success" onClick={addFavoritePen}>
            <FaStar /> Add Favorite
          </Button>
        </div>

        {showColorPicker && (
          <div className="color-picker-overlay">
            <SketchPicker color={color} onChangeComplete={(c) => setColor(c.hex)} />
          </div>
        )}

        <canvas
          ref={canvasRef}
          width={800}
          height={400}
          className={`drawingboard-canvas ${
            darkMode ? 'drawingboard-canvas-dark' : 'drawingboard-canvas-light'
          }`}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseOut={stopDrawing}
        />
      </div>
    </div>
  );
}

export default DrawingBoard;
